import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { MdClear as ExitIcon, MdSearch as SearchIcon } from 'react-icons/md';
import debounce from 'lodash/debounce';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';

import {
  MAIN_PAGE_NAME,
  BUTTONS_IN_SECTION,
  GETTING_STARTED_SECTION_NAME,
  ADDITIONAL_RESOURCES_SECTION_NAME,
  PAGE_HELP_SECTION_NAME,
  SEARCH_DOCUMENTATION_SECTION_NAME,
  RELEASE_NOTES_SECTION_NAME,
  SEARCH_TIMEOUT_DELAY,
} from './helpMenu.constants';

import { HelpContentPropTypes } from '../shared/proptypes';
import ContentButton from './contentButton.component';
import ContentButtonList from './contentButtonList.component';
import getPathnameForHelpContent from './utils/getPathnameForHelpContent';
import ImportExportButtons from './importExportButtons.component';
import Spinner from '../../shared/spinner.component';

import {
  searchInputChanged,
  searchForDocumentation,
} from './subsections/searchDocs/store/actions';

const SearchBar = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [searchText, setSearchText] = useState('');

  const searchForDocumentationDebounced = useMemo(() => debounce(searchText => {
    dispatch(searchForDocumentation(searchText));
    dispatch(searchInputChanged(searchText));
    history.push('/search');
  }, SEARCH_TIMEOUT_DELAY), [dispatch, history]);

  const onSearchChange = useCallback(e => {
    searchForDocumentationDebounced(e.target.value);
    setSearchText(e.target.value);
  }, [searchForDocumentationDebounced]);


  return (
    <div className="search-docs-wrapper">
      <label className="search-docs-bar border shadow-sm rounded">
        <SearchIcon />
        <input value={searchText} onInput={onSearchChange} className="search-docs-bar-input" autoFocus />
      </label>
    </div>
  );
};

const MainPage = ({
  helpContents,
  onClose,
  helpLoading,
  pageHelpLoading,
  deleteContent,
  deleteContentLoading,
  addContent,
  addContentLoading,
  contentEditable,
  contentsForExport,
  contentsForExportLoading,
  fetchAllContentsForExport,
  resetContentsForExport,
  sendContentsForImport,
  sendingContentsForImport,
  errorNotification,
}) => {
  const gettingStartedContents = helpContents
    .filter(contentItem => contentItem.section === GETTING_STARTED_SECTION_NAME)
    .slice(0, BUTTONS_IN_SECTION);

  const additionalResourcesContents = helpContents
    .filter(contentItem => contentItem.section === ADDITIONAL_RESOURCES_SECTION_NAME)
    .slice(0, BUTTONS_IN_SECTION);

  const pageHelpContents = helpContents.filter(contentItem => contentItem.section === null);

  const createAdditionalResourcesContent = useCallback(
    ({ title, settings }) => addContent({ title, sectionName: ADDITIONAL_RESOURCES_SECTION_NAME, settings }),
    [addContent]
  );

  const createGettingStartedContent = useCallback(
    ({ title, settings }) => addContent({ title, sectionName: GETTING_STARTED_SECTION_NAME, settings }),
    [addContent]
  );

  const createPageContent = useCallback(
    ({ title, settings }) => addContent({ pageUrl: getPathnameForHelpContent(), title, settings }),
    [addContent]
  );

  const handleDeleteContent = useCallback(
    contentId => event => {
      event.stopPropagation();
      deleteContent(contentId);
    },
    [deleteContent]
  );

  const showPageHelpSection = pageHelpContents.length || contentEditable;

  return (
    <>
      <h4 className="popup-header">
        <div className="header">
          <h5>{MAIN_PAGE_NAME}</h5>
          <div className="header-icons-container">
            <ImportExportButtons
              show={contentEditable}
              contentsForExport={contentsForExport}
              contentsForExportLoading={contentsForExportLoading}
              fetchAllContentsForExport={fetchAllContentsForExport}
              sendContentsForImport={sendContentsForImport}
              sendingContentsForImport={sendingContentsForImport}
              resetContentsForExport={resetContentsForExport}
              errorNotification={errorNotification}
            />
            <ExitIcon onClick={onClose} className="popup-header-exit" />
          </div>
        </div>
      </h4>
      {helpLoading
        ? <div className="popup-body"><Spinner /></div>
        : (
          <div className="popup-body">
            <SearchBar />
            <ContentButtonList
              sectionName={GETTING_STARTED_SECTION_NAME}
              helpContents={gettingStartedContents}
              addContent={contentEditable ? createGettingStartedContent : null}
              addContentLoading={addContentLoading}
              deleteContent={contentEditable ? handleDeleteContent : null}
            />
            {showPageHelpSection
              ? (
                <ContentButtonList
                  sectionName={PAGE_HELP_SECTION_NAME}
                  helpContents={pageHelpContents}
                  addContent={contentEditable ? createPageContent : null}
                  addContentLoading={addContentLoading}
                  deleteContent={contentEditable ? handleDeleteContent : null}
                  deleteContentLoading={deleteContentLoading}
                  loading={pageHelpLoading}
                />
              )
              : null
            }
            <ContentButtonList
              sectionName={ADDITIONAL_RESOURCES_SECTION_NAME}
              helpContents={additionalResourcesContents}
              addContent={contentEditable ? createAdditionalResourcesContent : null}
              addContentLoading={addContentLoading}
              deleteContent={contentEditable ? handleDeleteContent : null}
              additionalContent={
                <>
                  <ContentButton to={`/search`}>
                    {SEARCH_DOCUMENTATION_SECTION_NAME}
                  </ContentButton>
                  <ContentButton to={`/releaseNotes`}>
                    {RELEASE_NOTES_SECTION_NAME}
                  </ContentButton>
                </>
              }
            />
          </div>
        )}
    </>
  );
};

MainPage.propTypes = {
  helpContents: PropTypes.arrayOf(HelpContentPropTypes).isRequired,
  onClose: PropTypes.func.isRequired,
  helpLoading: PropTypes.bool.isRequired,
  pageHelpLoading: PropTypes.bool.isRequired,
  deleteContent: PropTypes.func.isRequired,
  deleteContentLoading: PropTypes.bool.isRequired,
  addContent: PropTypes.func.isRequired,
  addContentLoading: PropTypes.bool.isRequired,
  contentEditable: PropTypes.bool.isRequired,
  contentsForExport: PropTypes.arrayOf(PropTypes.shape({
    contentId: PropTypes.number.isRequired,
    icon: PropTypes.string,
    section: PropTypes.string,
    title: PropTypes.string.isRequired,
    content: PropTypes.string,
    contentText: PropTypes.string,
    pageUrl: PropTypes.string,
    pageName: PropTypes.string,
  })).isRequired,
  contentsForExportLoading: PropTypes.bool.isRequired,
  fetchAllContentsForExport: PropTypes.func.isRequired,
  resetContentsForExport: PropTypes.func.isRequired,
  sendContentsForImport: PropTypes.func.isRequired,
  sendingContentsForImport: PropTypes.bool.isRequired,
  errorNotification: PropTypes.func.isRequired,
};

export default MainPage;
